import { type Moment } from 'moment'
import { useCallback, useMemo } from 'react'
import {
  MergeableReportTypeEnum,
  ReportTypeEnum,
  useMergeNormalizedReportsMutation,
} from 'domains/reports/api'

export interface Props {
  partnerId: string
  retailerId: string
  reportType: ReportTypeEnum
  dateRange: { startDate: Moment; endDate: Moment }
}

const getMergeableReport = (reportType: ReportTypeEnum) => {
  switch (reportType) {
    case null:
      return null
    case undefined:
      return undefined
    case ReportTypeEnum.RetailerTaxLogs:
      return MergeableReportTypeEnum.RetailerTaxLogs
    case ReportTypeEnum.TaxesPaidAtPos:
      return MergeableReportTypeEnum.TaxesPaidAtPos
    // Add your new mergeable report types here
    default:
      throw new Error(`Unsupported ReportTypeEnum for merging: ${reportType}`)
  }
}

export const useMergeNormalizedReports = ({
  partnerId,
  retailerId,
  reportType,
  dateRange,
}: Props) => {
  const formattedDates = useMemo(() => {
    const format = 'YYYY-MM-DD'

    return {
      startDate: dateRange.startDate?.format(format),
      endDate: dateRange.endDate?.format(format),
    }
  }, [dateRange])

  const [triggerMutation, { data, loading, error }] = useMergeNormalizedReportsMutation()

  const createMergedReport = useCallback(() => {
    const mergeableReport = getMergeableReport(reportType)

    triggerMutation({
      variables: {
        partnerId,
        retailerId,
        reportType: mergeableReport,
        startDate: formattedDates.startDate,
        endDate: formattedDates.endDate,
      },
    })
  }, [triggerMutation, partnerId, retailerId, reportType, formattedDates])

  return {
    createMergedReport,
    apiSuccess: data ? Boolean(data?.normalizedReportsMerge.success) : null,
    apiLoading: loading,
    apiError: error,
  }
}
